import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs, { type Dayjs } from "dayjs";
import { campaignsApi } from "@/core/api/campaignsApi";
import { crmApi } from "@/core/api/crmApi";
import { tenantWorkspaceApi } from "@/core/api/tenantWorkspaceApi";
import { hasPermission, permissionCodes } from "@/core/auth/permissions";
import { useAuthStore } from "@/core/auth/authStore";
import type {
  Campaign,
  CampaignChannel,
  CampaignConsentStatus,
  CampaignContactConsent,
  CampaignSegment,
  CampaignSegmentRules,
  CampaignSettings,
  CampaignTemplate,
  CreateCampaignRequest,
  SaveCampaignSegmentRequest,
} from "@/core/types/campaigns";
import { formatDateTime } from "@/core/utils/date";
import { getErrorMessage } from "@/core/utils/errors";
import { AppIcon } from "@/shared/components/AppIcon";
import { JalaliDateTimePicker } from "@/shared/components/JalaliDateTimePicker";

type Tab =
  "campaigns" | "presets" | "segments" | "templates" | "consents" | "settings";
const retailJourneyCodes = new Set([
  "retail-reserved-no-purchase",
  "retail-post-purchase",
  "retail-interested-no-purchase",
  "retail-inactive",
  "retail-vip",
]);
const fa = (value: number): string =>
  new Intl.NumberFormat("fa-IR").format(value);
const tabs: Array<{ id: Tab; label: string }> = [
  { id: "campaigns", label: "کمپین‌ها" },
  { id: "presets", label: "سناریوهای پیشنهادی" },
  { id: "segments", label: "گروه‌های مخاطب" },
  { id: "templates", label: "قالب‌های پیام" },
  { id: "consents", label: "رضایت مخاطبان" },
  { id: "settings", label: "کنترل ارسال" },
];
const channelLabels: Record<string, string> = {
  sms: "پیامک",
  email: "ایمیل",
  social: "شبکه اجتماعی",
};
const typeLabels: Record<string, string> = {
  oneTime: "یک‌باره",
  scheduled: "زمان‌بندی‌شده",
  triggered: "رویدادمحور",
  recurring: "تکرارشونده",
};
const statusMeta: Record<string, { label: string; tone: string }> = {
  draft: { label: "پیش‌نویس", tone: "bg-slate-100 text-slate-600" },
  pendingApproval: { label: "منتظر تأیید", tone: "bg-amber-50 text-amber-700" },
  scheduled: { label: "زمان‌بندی‌شده", tone: "bg-blue-50 text-blue-700" },
  running: { label: "در حال اجرا", tone: "bg-violet-50 text-violet-700" },
  paused: { label: "متوقف", tone: "bg-orange-50 text-orange-700" },
  completed: { label: "پایان‌یافته", tone: "bg-emerald-50 text-emerald-700" },
  cancelled: { label: "لغوشده", tone: "bg-rose-50 text-rose-700" },
};
const recipientStatusLabels: Record<string, string> = {
  queued: "در صف",
  processing: "در حال ارسال",
  deferred: "در انتظار تلاش مجدد",
  sent: "ارسال‌شده",
  delivered: "تحویل‌شده",
  failed: "ناموفق",
  skipped: "ارسال‌نشده",
  suppressed: "متوقف‌شده توسط قواعد",
};
const interestOptions = [
  { value: "", label: "بدون شرط" },
  { value: "Viewed", label: "مشاهده کرده" },
  { value: "AskedPrice", label: "قیمت پرسیده" },
  { value: "Tried", label: "امتحان کرده" },
  { value: "Interested", label: "علاقه‌مند" },
  { value: "Reserved", label: "رزرو کرده" },
  { value: "Purchased", label: "خرید کرده" },
  { value: "Rejected", label: "رد کرده" },
];
const eventOptions = [
  { value: "", label: "بدون شرط" },
  { value: "LandingSent", label: "صفحه برای او ارسال شده" },
  { value: "LandingOpened", label: "صفحه را باز کرده" },
  { value: "ProductViewed", label: "کالا را دیده" },
  { value: "CTA_Clicked", label: "روی اقدام کلیک کرده" },
  { value: "Reserved", label: "رزرو کرده" },
  { value: "Purchased", label: "خرید کرده" },
];
const variables = [
  "{{customerName}}",
  "{{tenantName}}",
  "{{companyLink}}",
  "{{productName}}",
  "{{landingLink}}",
  "{{purchaseCount}}",
  "{{purchaseTotal}}",
  "{{purchaseCurrency}}",
  "{{lastPurchaseAt}}",
  "{{loyaltyPoints}}",
  "{{vipLevel}}",
  "{{mobile}}",
  "{{email}}",
];

const emptyCampaign = (): CreateCampaignRequest => ({
  name: "",
  description: "",
  type: "scheduled",
  channel: "sms",
  audienceType: "allCustomers",
  segmentId: null,
  presetCode: null,
  subject: "",
  messageTemplate:
    "سلام {{customerName}}، پیام جدید {{tenantName}} را مشاهده کنید: {{companyLink}}",
  isCommercial: true,
  triggerCode: null,
  triggerDelayMinutes: 0,
  recurrenceIntervalDays: 1,
  scheduledAtUtc: null,
  startsAtUtc: null,
  endsAtUtc: null,
  conversionEvent: "Purchased",
  attributionWindowDays: 30,
});
const emptyRules = (): CampaignSegmentRules => ({
  search: "",
  createdWithinDays: null,
  hasMobile: true,
  hasEmail: null,
  interestType: null,
  eventType: null,
  productId: null,
  activityWithinDays: null,
  inactiveForDays: null,
  excludePurchased: false,
  purchasedWithinDays: null,
  lastPurchaseBeforeDays: null,
  minimumPurchaseCount: null,
  minimumPurchaseAmount: null,
  purchaseCurrency: null,
  vipLevel: null,
  minimumVipLevel: null,
  minimumLoyaltyPoints: null,
});
const emptySegment = (): SaveCampaignSegmentRequest => ({
  name: "",
  description: "",
  mode: "dynamic",
  rules: emptyRules(),
  contactIds: [],
});
const emptyTemplate = (): Omit<CampaignTemplate, "id" | "isActive"> => ({
  name: "",
  channel: "sms",
  subject: "",
  body: "",
  isCommercial: true,
});

const Field = ({
  label,
  hint,
  children,
  className = "",
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
  className?: string;
}): React.ReactElement => (
  <label className={`block ${className}`}>
    <span className="mb-2 block text-xs font-black text-slate-600">
      {label}
    </span>
    {children}
    {hint ? (
      <span className="mt-1.5 block text-[11px] leading-5 text-slate-400">
        {hint}
      </span>
    ) : null}
  </label>
);
const Toggle = ({
  checked,
  label,
  hint,
  onChange,
}: {
  checked: boolean;
  label: string;
  hint?: string;
  onChange: (value: boolean) => void;
}): React.ReactElement => (
  <button
    className={`flex w-full items-center justify-between gap-3 rounded-2xl border p-4 text-right transition ${checked ? "border-brand-200 bg-brand-50/70" : "border-slate-200 bg-white"}`}
    onClick={() => onChange(!checked)}
    type="button"
  >
    <span>
      <b className="block text-sm text-slate-800">{label}</b>
      {hint ? (
        <small className="mt-1 block leading-5 text-slate-400">{hint}</small>
      ) : null}
    </span>
    <span
      className={`relative h-6 w-11 shrink-0 rounded-full transition ${checked ? "bg-brand-600" : "bg-slate-300"}`}
    >
      <span
        className={`absolute top-1 size-4 rounded-full bg-white transition ${checked ? "right-6" : "right-1"}`}
      />
    </span>
  </button>
);
const Modal = ({
  title,
  subtitle,
  onClose,
  children,
  wide = false,
}: {
  title: string;
  subtitle?: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}): React.ReactElement => (
  <div
    className="fixed inset-0 z-[80] grid place-items-end bg-slate-950/45 p-0 backdrop-blur-sm sm:place-items-center sm:p-5"
    role="dialog"
  >
    <section
      className={`max-h-[94vh] w-full overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl ${wide ? "sm:max-w-5xl" : "sm:max-w-2xl"}`}
    >
      <header className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-100 bg-white/95 p-5 backdrop-blur sm:p-6">
        <div>
          <h2 className="text-lg font-black text-ink-950">{title}</h2>
          {subtitle ? (
            <p className="mt-1 text-xs leading-6 text-slate-400">{subtitle}</p>
          ) : null}
        </div>
        <button
          aria-label="بستن"
          className="grid size-10 shrink-0 place-items-center rounded-xl bg-slate-100 text-slate-500"
          onClick={onClose}
          type="button"
        >
          <AppIcon name="close" size={18} />
        </button>
      </header>
      <div className="p-5 sm:p-6">{children}</div>
    </section>
  </div>
);

export const CampaignsPage = (): React.ReactElement => {
  const queryClient = useQueryClient();
  const permissions = useAuthStore((state) => state.permissions);
  const canManage = hasPermission(permissions, permissionCodes.campaignsManage);
  const canSend = hasPermission(permissions, permissionCodes.campaignsSend);
  const canApprove = hasPermission(
    permissions,
    permissionCodes.campaignsApprove,
  );
  const [tab, setTab] = useState<Tab>("campaigns");
  const [campaignEditor, setCampaignEditor] = useState<Campaign | "new" | null>(
    null,
  );
  const [campaignForm, setCampaignForm] =
    useState<CreateCampaignRequest>(emptyCampaign());
  const [segmentEditor, setSegmentEditor] = useState<
    CampaignSegment | "new" | null
  >(null);
  const [segmentForm, setSegmentForm] =
    useState<SaveCampaignSegmentRequest>(emptySegment());
  const [templateEditor, setTemplateEditor] = useState<
    CampaignTemplate | "new" | null
  >(null);
  const [templateForm, setTemplateForm] = useState(emptyTemplate());
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [recipientsId, setRecipientsId] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<{
    tone: "ok" | "error";
    text: string;
  } | null>(null);

  const dashboard = useQuery({
    queryKey: ["campaigns", "dashboard"],
    queryFn: campaignsApi.dashboard,
  });
  const campaigns = useQuery({
    queryKey: ["campaigns"],
    queryFn: campaignsApi.list,
  });
  const segments = useQuery({
    queryKey: ["campaigns", "segments"],
    queryFn: campaignsApi.segments,
  });
  const templates = useQuery({
    queryKey: ["campaigns", "templates"],
    queryFn: campaignsApi.templates,
  });
  const presets = useQuery({
    queryKey: ["campaigns", "presets"],
    queryFn: campaignsApi.presets,
  });
  const settings = useQuery({
    queryKey: ["campaigns", "settings"],
    queryFn: campaignsApi.settings,
  });
  const suppressions = useQuery({
    queryKey: ["campaigns", "suppressions"],
    queryFn: campaignsApi.suppressions,
  });
  const contacts = useQuery({
    queryKey: ["contacts", "campaign-picker"],
    queryFn: () => crmApi.contacts({ page: 1, pageSize: 100 }),
    enabled: segmentEditor !== null && segmentForm.mode === "static",
  });
  const catalogItems = useQuery({
    queryKey: ["tenant-catalog", "campaign-segment"],
    queryFn: tenantWorkspaceApi.catalog,
    enabled: segmentEditor !== null && segmentForm.mode === "dynamic",
  });
  const preview = useQuery({
    queryKey: ["campaigns", previewId, "preview"],
    queryFn: () => campaignsApi.preview(previewId!),
    enabled: Boolean(previewId),
  });
  const recipientRows = useQuery({
    queryKey: ["campaigns", recipientsId, "recipients"],
    queryFn: () => campaignsApi.recipients(recipientsId!),
    enabled: Boolean(recipientsId),
  });

  const refresh = async (): Promise<void> => {
    await Promise.all(
      [
        "campaigns",
        "dashboard",
        "segments",
        "templates",
        "presets",
        "settings",
        "suppressions",
      ].map((key) =>
        queryClient.invalidateQueries({
          queryKey: key === "campaigns" ? ["campaigns"] : ["campaigns", key],
        }),
      ),
    );
  };
  const saveCampaign = useMutation({
    mutationFn: () =>
      campaignEditor === "new"
        ? campaignsApi.create(campaignForm)
        : campaignsApi.update((campaignEditor as Campaign).id, campaignForm),
    onSuccess: async () => {
      setCampaignEditor(null);
      setFeedback({
        tone: "ok",
        text: "پیش‌نویس کمپین ذخیره شد. پیش از انتشار، مخاطبان را پیش‌نمایش کنید.",
      });
      await refresh();
    },
    onError: (error) =>
      setFeedback({ tone: "error", text: getErrorMessage(error) }),
  });
  const campaignAction = useMutation({
    mutationFn: ({
      id,
      action,
    }: {
      id: string;
      action: "publish" | "approve" | "pause" | "resume" | "cancel";
    }) => campaignsApi.action(id, action),
    onSuccess: async (_, input) => {
      setFeedback({
        tone: "ok",
        text:
          input.action === "publish"
            ? "کمپین برای تأیید یا اجرا ثبت شد."
            : "وضعیت کمپین تغییر کرد.",
      });
      await refresh();
    },
    onError: (error) =>
      setFeedback({ tone: "error", text: getErrorMessage(error) }),
  });
  const saveSegment = useMutation({
    mutationFn: () =>
      campaignsApi.saveSegment(
        segmentEditor === "new" ? null : (segmentEditor as CampaignSegment).id,
        segmentForm,
      ),
    onSuccess: async () => {
      setSegmentEditor(null);
      setFeedback({ tone: "ok", text: "گروه مخاطبان ذخیره شد." });
      await refresh();
    },
    onError: (error) =>
      setFeedback({ tone: "error", text: getErrorMessage(error) }),
  });
  const saveTemplate = useMutation({
    mutationFn: () =>
      campaignsApi.saveTemplate(
        templateEditor === "new"
          ? null
          : (templateEditor as CampaignTemplate).id,
        templateForm,
      ),
    onSuccess: async () => {
      setTemplateEditor(null);
      setFeedback({ tone: "ok", text: "قالب پیام ذخیره شد." });
      await refresh();
    },
    onError: (error) =>
      setFeedback({ tone: "error", text: getErrorMessage(error) }),
  });
  const createPreset = useMutation({
    mutationFn: campaignsApi.createFromPreset,
    onSuccess: async () => {
      setTab("campaigns");
      setFeedback({
        tone: "ok",
        text: "سناریو به‌صورت پیش‌نویس ساخته شد؛ متن و مخاطبان را بررسی و سپس منتشر کنید.",
      });
      await refresh();
    },
    onError: (error) =>
      setFeedback({ tone: "error", text: getErrorMessage(error) }),
  });
  const activateJourney = useMutation({
    mutationFn: campaignsApi.activatePresetJourney,
    onSuccess: async (journey) => {
      setTab("campaigns");
      setFeedback({
        tone: "ok",
        text:
          journey.status === "pendingApproval"
            ? "Journey خودکار ثبت شد و منتظر تأیید اجراست."
            : "Journey خودکار فعال شد و رویدادهای جدید بر اساس شرایط این سناریو پیگیری می‌شوند.",
      });
      await refresh();
    },
    onError: (error) =>
      setFeedback({ tone: "error", text: getErrorMessage(error) }),
  });

  const openCampaign = (row?: Campaign): void => {
    setCampaignEditor(row ?? "new");
    setCampaignForm(
      row
        ? {
            name: row.name,
            description: row.description,
            type: row.type,
            channel: row.channel,
            audienceType: row.audienceType,
            segmentId: row.segmentId,
            presetCode: row.presetCode,
            subject: row.subject,
            messageTemplate: row.messageTemplate,
            isCommercial: row.isCommercial,
            triggerCode: row.triggerCode,
            triggerDelayMinutes: row.triggerDelayMinutes,
            recurrenceIntervalDays: row.recurrenceIntervalDays,
            scheduledAtUtc: row.scheduledAtUtc,
            startsAtUtc: row.startsAtUtc,
            endsAtUtc: row.endsAtUtc,
            conversionEvent: row.conversionEvent,
            attributionWindowDays: row.attributionWindowDays,
          }
        : emptyCampaign(),
    );
  };
  const openSegment = (row?: CampaignSegment): void => {
    setSegmentEditor(row ?? "new");
    setSegmentForm(
      row
        ? {
            name: row.name,
            description: row.description,
            mode: row.mode,
            rules: row.rules,
            contactIds: row.contactIds,
          }
        : emptySegment(),
    );
  };
  const openTemplate = (row?: CampaignTemplate): void => {
    setTemplateEditor(row ?? "new");
    setTemplateForm(
      row
        ? {
            name: row.name,
            channel: row.channel,
            subject: row.subject,
            body: row.body,
            isCommercial: row.isCommercial,
          }
        : emptyTemplate(),
    );
  };
  const selectedTemplate = (id: string): void => {
    const row = templates.data?.find((x) => x.id === id);
    if (row)
      setCampaignForm((current) => ({
        ...current,
        channel: row.channel,
        subject: row.subject,
        messageTemplate: row.body,
        isCommercial: row.isCommercial,
      }));
  };
  const scheduledValue = campaignForm.scheduledAtUtc
    ? dayjs(campaignForm.scheduledAtUtc)
    : undefined;
  const startsValue = campaignForm.startsAtUtc
    ? dayjs(campaignForm.startsAtUtc)
    : undefined;
  const endsValue = campaignForm.endsAtUtc
    ? dayjs(campaignForm.endsAtUtc)
    : undefined;

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-3xl bg-gradient-to-bl from-slate-950 via-indigo-950 to-brand-800 p-5 text-white shadow-xl shadow-indigo-950/10 sm:p-7">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <span className="inline-flex rounded-full bg-white/10 px-3 py-1 text-[11px] font-black text-indigo-100">
              مرکز تعامل هدفمند مشتری
            </span>
            <h1 className="mt-3 text-2xl font-black sm:text-3xl">
              مدیریت کمپین‌ها
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-7 text-indigo-100/80">
              از انتخاب مخاطب تا ارسال، رضایت، محدودیت دفعات و سنجش تبدیل در یک
              جریان کنترل‌شده.
            </p>
          </div>
          {canManage ? (
            <button
              className="inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-5 py-3 text-sm font-black text-indigo-950 shadow-lg transition hover:-translate-y-0.5"
              onClick={() => openCampaign()}
              type="button"
            >
              <AppIcon name="plus" size={18} />
              ساخت کمپین
            </button>
          ) : null}
        </div>
        <div className="mt-7 grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[
            ["کمپین فعال", dashboard.data?.activeCampaigns ?? 0],
            ["منتظر تأیید", dashboard.data?.pendingApproval ?? 0],
            ["ارسال موفق", dashboard.data?.sent ?? 0],
            ["تبدیل ثبت‌شده", dashboard.data?.converted ?? 0],
          ].map(([label, value]) => (
            <div
              className="rounded-2xl border border-white/10 bg-white/10 p-4 backdrop-blur"
              key={String(label)}
            >
              <p className="text-[11px] font-bold text-indigo-100/70">
                {label}
              </p>
              <p className="mt-2 text-2xl font-black">{fa(Number(value))}</p>
            </div>
          ))}
        </div>
      </section>

      {feedback ? (
        <div
          className={`flex items-start justify-between gap-3 rounded-2xl border p-4 text-sm font-bold ${feedback.tone === "ok" ? "border-emerald-100 bg-emerald-50 text-emerald-700" : "border-rose-100 bg-rose-50 text-rose-700"}`}
        >
          <span>{feedback.text}</span>
          <button onClick={() => setFeedback(null)} type="button">
            ×
          </button>
        </div>
      ) : null}

      <nav className="flex gap-2 overflow-x-auto rounded-2xl border border-slate-200 bg-white p-2 shadow-sm">
        {tabs.map((item) => (
          <button
            className={`shrink-0 rounded-xl px-4 py-2.5 text-xs font-black transition ${tab === item.id ? "bg-slate-950 text-white" : "text-slate-500 hover:bg-slate-50"}`}
            key={item.id}
            onClick={() => setTab(item.id)}
            type="button"
          >
            {item.label}
          </button>
        ))}
      </nav>

      {tab === "campaigns" ? (
        <CampaignList
          campaigns={campaigns.data ?? []}
          isLoading={campaigns.isLoading}
          canApprove={canApprove}
          canManage={canManage}
          canSend={canSend}
          onAction={(id, action) => campaignAction.mutate({ id, action })}
          onEdit={openCampaign}
          onPreview={setPreviewId}
          onRecipients={setRecipientsId}
        />
      ) : null}
      {tab === "presets" ? (
        <section>
          <SectionTitle
            title="سناریوهای پیشنهادی کسب‌وکار"
            description="این پیشنهادها بر اساس نوع کسب‌وکار شرکت نمایش داده می‌شوند و بعد از ساخت کاملاً قابل ویرایش‌اند."
          />
          {presets.isLoading ? (
            <Skeleton />
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {presets.data?.map((item) => (
                <article
                  className={`flex flex-col rounded-3xl border bg-white p-5 shadow-sm ${retailJourneyCodes.has(item.code) ? "border-brand-200 ring-1 ring-brand-100" : "border-slate-200"}`}
                  key={item.code}
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="grid size-11 place-items-center rounded-2xl bg-violet-50 text-violet-600">
                      <AppIcon name="sparkles" />
                    </span>
                    <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[10px] font-black text-slate-500">
                      {typeLabels[item.campaignType]}
                    </span>
                  </div>
                  <h3 className="mt-4 font-black text-ink-950">{item.name}</h3>
                  <p className="mt-2 flex-1 text-xs leading-6 text-slate-500">
                    {item.description}
                  </p>
                  {item.code === "retail-reserved-no-purchase" ? (
                    <div className="mt-4 rounded-2xl bg-brand-50 px-3 py-2.5 text-xs font-bold leading-6 text-brand-800">
                      بازدید کالا ← رزرو مشتری ← نبود خرید تا زمان انتخاب‌شده ← ارسال پیگیری خودکار
                    </div>
                  ) : item.code === "retail-post-purchase" ? (
                    <div className="mt-4 rounded-2xl bg-brand-50 px-3 py-2.5 text-xs font-bold leading-6 text-brand-800">
                      خرید ثبت‌شده ← تأخیر انتخاب‌شده ← پیگیری رضایت و پیشنهاد تکمیلی
                    </div>
                  ) : item.code === "retail-interested-no-purchase" ? (
                    <div className="mt-4 rounded-2xl bg-brand-50 px-3 py-2.5 text-xs font-bold leading-6 text-brand-800">
                      علاقه‌مندی مشتری ← نبود خرید ← ارسال یک پیگیری محترمانه
                    </div>
                  ) : item.code === "retail-inactive" ? (
                    <div className="mt-4 rounded-2xl bg-brand-50 px-3 py-2.5 text-xs font-bold leading-6 text-brand-800">
                      حداقل ۶۰ روز بدون تعامل یا خرید ← پیشنهاد بازگشت ← ارزیابی دوره‌ای
                    </div>
                  ) : item.code === "retail-vip" ? (
                    <div className="mt-4 rounded-2xl bg-brand-50 px-3 py-2.5 text-xs font-bold leading-6 text-brand-800">
                      مشتری نقره‌ای، طلایی یا پلاتینی ← پیشنهاد اختصاصی ← ارزیابی دوره‌ای
                    </div>
                  ) : null}
                  <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
                    <span className="text-xs font-bold text-slate-400">
                      {channelLabels[item.channel]}
                      {item.triggerDelayMinutes
                        ? ` · پس از ${fa(Math.round(item.triggerDelayMinutes / 60))} ساعت`
                        : item.campaignType === "recurring"
                          ? ` · هر ${fa(item.recurrenceIntervalDays)} روز`
                          : ""}
                    </span>
                    {(item.campaignType === "triggered" || item.campaignType === "recurring") && canSend ? (
                      <button
                        className="rounded-xl bg-brand-600 px-4 py-2 text-xs font-black text-white disabled:opacity-50"
                        disabled={activateJourney.isPending}
                        onClick={() => activateJourney.mutate(item.code)}
                        type="button"
                      >
                        {activateJourney.isPending ? "در حال فعال‌سازی…" : "فعال‌سازی خودکار"}
                      </button>
                    ) : canManage ? (
                      <button
                        className="rounded-xl bg-violet-600 px-4 py-2 text-xs font-black text-white disabled:opacity-50"
                        disabled={createPreset.isPending}
                        onClick={() => createPreset.mutate(item.code)}
                        type="button"
                      >
                        ساخت پیش‌نویس
                      </button>
                    ) : null}
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      ) : null}
      {tab === "segments" ? (
        <section>
          <SectionTitle
            action={
              canManage ? (
                <button
                  className="btn-primary"
                  onClick={() => openSegment()}
                  type="button"
                >
                  + گروه جدید
                </button>
              ) : undefined
            }
            title="گروه‌های مخاطب"
            description="گروه‌های پویا خودکار به‌روز می‌شوند؛ گروه ثابت فقط اعضای انتخاب‌شده را نگه می‌دارد."
          />
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {segments.data?.map((row) => (
              <article
                className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
                key={row.id}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-black text-ink-950">{row.name}</h3>
                    <p className="mt-1 text-xs text-slate-400">
                      {row.mode === "dynamic"
                        ? "پویا و به‌روزشونده"
                        : `${fa(row.staticMemberCount)} عضو ثابت`}
                    </p>
                  </div>
                  <span
                    className={`rounded-full px-2.5 py-1 text-[10px] font-black ${row.mode === "dynamic" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}
                  >
                    {row.mode === "dynamic" ? "پویا" : "ثابت"}
                  </span>
                </div>
                <p className="mt-3 min-h-12 text-xs leading-6 text-slate-500">
                  {row.description || "بدون توضیح"}
                </p>
                {canManage ? (
                  <button
                    className="mt-4 w-full rounded-xl bg-slate-950 py-2.5 text-xs font-black text-white"
                    onClick={() => openSegment(row)}
                    type="button"
                  >
                    ویرایش گروه
                  </button>
                ) : null}
              </article>
            ))}
          </div>
          {!segments.isLoading && !segments.data?.length ? (
            <Empty text="هنوز گروه مخاطبی ساخته نشده است." />
          ) : null}
        </section>
      ) : null}
      {tab === "templates" ? (
        <section>
          <SectionTitle
            action={
              canManage ? (
                <button
                  className="btn-primary"
                  onClick={() => openTemplate()}
                  type="button"
                >
                  + قالب جدید
                </button>
              ) : undefined
            }
            title="قالب‌های پیام"
            description="قالب را یک‌بار بسازید و در کمپین‌های مختلف استفاده کنید."
          />
          <div className="grid gap-4 lg:grid-cols-2">
            {templates.data?.map((row) => (
              <article
                className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm"
                key={row.id}
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="font-black text-ink-950">{row.name}</h3>
                  <span className="rounded-full bg-brand-50 px-2.5 py-1 text-[10px] font-black text-brand-700">
                    {channelLabels[row.channel]}
                  </span>
                </div>
                <p className="mt-3 line-clamp-3 min-h-16 whitespace-pre-wrap text-xs leading-6 text-slate-500">
                  {row.body}
                </p>
                {canManage ? (
                  <button
                    className="mt-4 rounded-xl bg-slate-100 px-4 py-2 text-xs font-black text-slate-700"
                    onClick={() => openTemplate(row)}
                    type="button"
                  >
                    ویرایش
                  </button>
                ) : null}
              </article>
            ))}
          </div>
          {!templates.isLoading && !templates.data?.length ? (
            <Empty text="هنوز قالب اختصاصی ساخته نشده است." />
          ) : null}
        </section>
      ) : null}
      {tab === "consents" ? (
        <ConsentsPanel canManage={canManage} onFeedback={setFeedback} />
      ) : null}
      {tab === "settings" && settings.data ? (
        <SettingsPanel
          canManage={canManage}
          initial={settings.data}
          suppressions={suppressions.data ?? []}
          onSaved={refresh}
          onFeedback={setFeedback}
        />
      ) : null}

      {campaignEditor ? (
        <Modal
          onClose={() => setCampaignEditor(null)}
          subtitle="کمپین ابتدا به‌صورت پیش‌نویس ذخیره می‌شود و بدون پیش‌نمایش و تأیید ارسال نخواهد شد."
          title={campaignEditor === "new" ? "ساخت کمپین جدید" : "ویرایش کمپین"}
          wide
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="نام کمپین">
              <input
                autoFocus
                className="input"
                maxLength={200}
                onChange={(e) =>
                  setCampaignForm((x) => ({ ...x, name: e.target.value }))
                }
                value={campaignForm.name}
              />
            </Field>
            <Field label="قالب آماده">
              <select
                className="input bg-white"
                defaultValue=""
                onChange={(e) => selectedTemplate(e.target.value)}
              >
                <option value="">انتخاب قالب…</option>
                {templates.data?.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="نوع اجرا">
              <select
                className="input bg-white"
                onChange={(e) =>
                  setCampaignForm((x) => ({
                    ...x,
                    type: e.target.value as CreateCampaignRequest["type"],
                  }))
                }
                value={campaignForm.type}
              >
                <option value="oneTime">ارسال یک‌باره</option>
                <option value="scheduled">زمان‌بندی‌شده</option>
                <option value="triggered">رویدادمحور</option>
                <option value="recurring">تکرارشونده دوره‌ای</option>
              </select>
            </Field>
            <Field label="کانال">
              <select
                className="input bg-white"
                onChange={(e) =>
                  setCampaignForm((x) => ({
                    ...x,
                    channel: e.target.value as CampaignChannel,
                  }))
                }
                value={campaignForm.channel}
              >
                <option value="sms">پیامک</option>
                <option value="email">ایمیل</option>
                <option value="social">شبکه اجتماعی / Webhook</option>
              </select>
            </Field>
            <Field label="مخاطبان">
              <select
                className="input bg-white"
                onChange={(e) =>
                  setCampaignForm((x) => ({
                    ...x,
                    audienceType: e.target
                      .value as CreateCampaignRequest["audienceType"],
                    segmentId:
                      e.target.value === "allCustomers" ? null : x.segmentId,
                  }))
                }
                value={campaignForm.audienceType}
              >
                <option value="allCustomers">همه مشتریان فعال</option>
                <option value="segment">گروه مخاطب</option>
              </select>
            </Field>
            {campaignForm.audienceType !== "allCustomers" ? (
              <Field label="گروه انتخابی">
                <select
                  className="input bg-white"
                  onChange={(e) =>
                    setCampaignForm((x) => ({
                      ...x,
                      segmentId: e.target.value || null,
                    }))
                  }
                  value={campaignForm.segmentId ?? ""}
                >
                  <option value="">انتخاب کنید…</option>
                  {segments.data?.map((x) => (
                    <option key={x.id} value={x.id}>
                      {x.name}
                    </option>
                  ))}
                </select>
              </Field>
            ) : (
              <div />
            )}
            {campaignForm.type === "scheduled" ||
            campaignForm.type === "oneTime" ? (
              <Field label="زمان اجرا">
                <JalaliDateTimePicker
                  onChange={(value?: Dayjs) =>
                    setCampaignForm((x) => ({
                      ...x,
                      scheduledAtUtc: value?.toISOString() ?? null,
                    }))
                  }
                  placeholder="زمان ارسال را به شمسی انتخاب کنید"
                  value={scheduledValue}
                />
              </Field>
            ) : null}
            {campaignForm.type === "triggered" ||
            campaignForm.type === "recurring" ? (
              <>
                <Field label="شروع بازه اجرا">
                  <JalaliDateTimePicker
                    onChange={(value?: Dayjs) =>
                      setCampaignForm((x) => ({
                        ...x,
                        startsAtUtc: value?.toISOString() ?? null,
                      }))
                    }
                    placeholder="شروع را به شمسی انتخاب کنید"
                    value={startsValue}
                  />
                </Field>
                <Field label="پایان بازه اجرا">
                  <JalaliDateTimePicker
                    onChange={(value?: Dayjs) =>
                      setCampaignForm((x) => ({
                        ...x,
                        endsAtUtc: value?.toISOString() ?? null,
                      }))
                    }
                    placeholder="اختیاری؛ پایان را به شمسی انتخاب کنید"
                    value={endsValue}
                  />
                </Field>
              </>
            ) : null}
            {campaignForm.type === "triggered" ? (
              <>
                <Field label="کد رویداد">
                  <select
                    className="input bg-white"
                    onChange={(e) =>
                      setCampaignForm((x) => ({
                        ...x,
                        triggerCode: e.target.value,
                      }))
                    }
                    value={campaignForm.triggerCode ?? ""}
                  >
                    <option value="">انتخاب کنید…</option>
                    <option value="LANDING_OPENED_NO_RESERVATION">
                      مشاهده بدون رزرو
                    </option>
                    <option value="RESERVED_NO_PURCHASE">رزرو بدون خرید</option>
                    <option value="PURCHASED_FOLLOW_UP">پیگیری پس از خرید</option>
                    <option value="INTERESTED_NO_PURCHASE">علاقه‌مندی بدون خرید</option>
                    <option value="CUSTOMER_ACTIVITY">تعامل مشتری</option>
                  </select>
                </Field>
                <Field label="تأخیر اجرا (دقیقه)">
                  <input
                    className="input"
                    min={0}
                    onChange={(e) =>
                      setCampaignForm((x) => ({
                        ...x,
                        triggerDelayMinutes: Number(e.target.value),
                      }))
                    }
                    type="number"
                    value={campaignForm.triggerDelayMinutes}
                  />
                </Field>
              </>
            ) : null}
            {campaignForm.type === "recurring" ? (
              <Field
                hint="مثلاً ۱ برای روزانه، ۷ برای هفتگی و ۳۰ برای ماهانه"
                label="فاصله تکرار (روز)"
              >
                <input
                  className="input"
                  max={365}
                  min={1}
                  onChange={(e) =>
                    setCampaignForm((x) => ({
                      ...x,
                      recurrenceIntervalDays: Number(e.target.value),
                    }))
                  }
                  type="number"
                  value={campaignForm.recurrenceIntervalDays}
                />
              </Field>
            ) : null}
            <Field label="رویداد هدف (تبدیل)">
              <select
                className="input bg-white"
                onChange={(e) =>
                  setCampaignForm((x) => ({
                    ...x,
                    conversionEvent: e.target.value || null,
                  }))
                }
                value={campaignForm.conversionEvent ?? ""}
              >
                <option value="">فقط سنجش بازدید و کلیک</option>
                <option value="CTA_Clicked">کلیک روی اقدام</option>
                <option value="Reserved">رزرو کالا یا خدمت</option>
                <option value="Purchased">خرید نهایی</option>
              </select>
            </Field>
            <Field
              hint="رزرو یا خرید تا این تعداد روز پس از ارسال، نتیجه همین کمپین محسوب می‌شود."
              label="بازه انتساب نتیجه (روز)"
            >
              <input
                className="input"
                max={365}
                min={1}
                onChange={(e) =>
                  setCampaignForm((x) => ({
                    ...x,
                    attributionWindowDays: Number(e.target.value),
                  }))
                }
                type="number"
                value={campaignForm.attributionWindowDays}
              />
            </Field>
            <Field className="md:col-span-2" label="موضوع ایمیل / عنوان گزارش">
              <input
                className="input"
                maxLength={200}
                onChange={(e) =>
                  setCampaignForm((x) => ({ ...x, subject: e.target.value }))
                }
                value={campaignForm.subject}
              />
            </Field>
            <Field className="md:col-span-2" label="متن پیام">
              <textarea
                className="input min-h-40 resize-y leading-8"
                maxLength={4000}
                onChange={(e) =>
                  setCampaignForm((x) => ({
                    ...x,
                    messageTemplate: e.target.value,
                  }))
                }
                value={campaignForm.messageTemplate}
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {variables.map((token) => (
                  <button
                    className="rounded-lg bg-slate-100 px-2 py-1 font-sans text-[10px] font-bold text-slate-600"
                    key={token}
                    onClick={() =>
                      setCampaignForm((x) => ({
                        ...x,
                        messageTemplate: `${x.messageTemplate} ${token}`,
                      }))
                    }
                    type="button"
                  >
                    {token}
                  </button>
                ))}
              </div>
            </Field>
            <Field className="md:col-span-2" label="توضیحات داخلی">
              <textarea
                className="input min-h-20 resize-y"
                onChange={(e) =>
                  setCampaignForm((x) => ({
                    ...x,
                    description: e.target.value,
                  }))
                }
                value={campaignForm.description ?? ""}
              />
            </Field>
            <div className="md:col-span-2">
              <Toggle
                checked={campaignForm.isCommercial}
                hint="پیام تجاری فقط برای مخاطبانی ارسال می‌شود که رضایت معتبر دارند."
                label="پیام بازاریابی و تجاری"
                onChange={(value) =>
                  setCampaignForm((x) => ({ ...x, isCommercial: value }))
                }
              />
            </div>
          </div>
          {saveCampaign.isError ? (
            <p className="mt-4 rounded-xl bg-rose-50 p-3 text-xs font-bold text-rose-700">
              {getErrorMessage(saveCampaign.error)}
            </p>
          ) : null}
          <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button
              className="rounded-xl bg-slate-100 px-5 py-3 text-sm font-black text-slate-600"
              onClick={() => setCampaignEditor(null)}
              type="button"
            >
              انصراف
            </button>
            <button
              className="rounded-xl bg-brand-600 px-6 py-3 text-sm font-black text-white disabled:opacity-50"
              disabled={
                saveCampaign.isPending ||
                !campaignForm.name.trim() ||
                !campaignForm.messageTemplate.trim()
              }
              onClick={() => saveCampaign.mutate()}
              type="button"
            >
              {saveCampaign.isPending ? "در حال ذخیره…" : "ذخیره پیش‌نویس"}
            </button>
          </div>
        </Modal>
      ) : null}

      {segmentEditor ? (
        <Modal
          onClose={() => setSegmentEditor(null)}
          subtitle="شرط‌ها با هم ترکیب می‌شوند؛ تنها مشتریانی که همه شرط‌ها را داشته باشند عضو گروه خواهند بود."
          title={segmentEditor === "new" ? "ساخت گروه مخاطب" : "ویرایش گروه"}
          wide
        >
          <div className="grid gap-4 md:grid-cols-2">
            <Field label="نام گروه">
              <input
                autoFocus
                className="input"
                onChange={(e) =>
                  setSegmentForm((x) => ({ ...x, name: e.target.value }))
                }
                value={segmentForm.name}
              />
            </Field>
            <Field label="نوع گروه">
              <select
                className="input bg-white"
                onChange={(e) =>
                  setSegmentForm((x) => ({
                    ...x,
                    mode: e.target.value as "dynamic" | "static",
                  }))
                }
                value={segmentForm.mode}
              >
                <option value="dynamic">پویا</option>
                <option value="static">ثابت</option>
              </select>
            </Field>
            <Field className="md:col-span-2" label="توضیحات">
              <input
                className="input"
                onChange={(e) =>
                  setSegmentForm((x) => ({ ...x, description: e.target.value }))
                }
                value={segmentForm.description ?? ""}
              />
            </Field>
            {segmentForm.mode === "dynamic" ? (
              <>
                <Field label="جست‌وجو در مخاطبان">
                  <input
                    className="input"
                    onChange={(e) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: { ...x.rules, search: e.target.value || null },
                      }))
                    }
                    placeholder="نام، موبایل یا ایمیل"
                    value={segmentForm.rules.search ?? ""}
                  />
                </Field>
                <Field label="کالا یا خدمت مشخص">
                  <select
                    className="input bg-white"
                    onChange={(e) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: {
                          ...x.rules,
                          productId: e.target.value || null,
                        },
                      }))
                    }
                    value={segmentForm.rules.productId ?? ""}
                  >
                    <option value="">همه کالاها و خدمات</option>
                    {catalogItems.data?.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.name}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="ثبت‌شده در چند روز اخیر">
                  <input
                    className="input"
                    min={1}
                    onChange={(e) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: {
                          ...x.rules,
                          createdWithinDays: e.target.value
                            ? Number(e.target.value)
                            : null,
                        },
                      }))
                    }
                    placeholder="بدون محدودیت"
                    type="number"
                    value={segmentForm.rules.createdWithinDays ?? ""}
                  />
                </Field>
                <Field label="غیرفعال برای چند روز">
                  <input
                    className="input"
                    min={1}
                    onChange={(e) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: {
                          ...x.rules,
                          inactiveForDays: e.target.value
                            ? Number(e.target.value)
                            : null,
                        },
                      }))
                    }
                    placeholder="مثلاً ۶۰"
                    type="number"
                    value={segmentForm.rules.inactiveForDays ?? ""}
                  />
                </Field>
                <Field label="تعامل در چند روز اخیر">
                  <input
                    className="input"
                    min={1}
                    onChange={(e) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: {
                          ...x.rules,
                          activityWithinDays: e.target.value
                            ? Number(e.target.value)
                            : null,
                        },
                      }))
                    }
                    placeholder="بدون محدودیت"
                    type="number"
                    value={segmentForm.rules.activityWithinDays ?? ""}
                  />
                </Field>
                <Field
                  label="خرید در چند روز اخیر"
                  hint="فقط مشتریانی که در این بازه حداقل یک فاکتور تکمیل‌شده دارند."
                >
                  <input
                    className="input"
                    min={1}
                    onChange={(e) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: {
                          ...x.rules,
                          purchasedWithinDays: e.target.value
                            ? Number(e.target.value)
                            : null,
                        },
                      }))
                    }
                    placeholder="مثلاً ۳۰"
                    type="number"
                    value={segmentForm.rules.purchasedWithinDays ?? ""}
                  />
                </Field>
                <Field
                  label="آخرین خرید قدیمی‌تر از"
                  hint="برای بازگرداندن مشتریانی که مدت‌هاست خرید نکرده‌اند."
                >
                  <input
                    className="input"
                    min={1}
                    onChange={(e) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: {
                          ...x.rules,
                          lastPurchaseBeforeDays: e.target.value
                            ? Number(e.target.value)
                            : null,
                        },
                      }))
                    }
                    placeholder="مثلاً ۶۰ روز"
                    type="number"
                    value={segmentForm.rules.lastPurchaseBeforeDays ?? ""}
                  />
                </Field>
                <Field
                  label="حداقل تعداد خرید"
                  hint="تعداد فاکتورهای تکمیل‌شده مشتری."
                >
                  <input
                    className="input"
                    min={1}
                    onChange={(e) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: {
                          ...x.rules,
                          minimumPurchaseCount: e.target.value
                            ? Number(e.target.value)
                            : null,
                        },
                      }))
                    }
                    placeholder="مثلاً ۳"
                    type="number"
                    value={segmentForm.rules.minimumPurchaseCount ?? ""}
                  />
                </Field>
                <Field
                  label="حداقل مبلغ خرید"
                  hint="مجموع فاکتورهای تکمیل‌شده در ارز انتخاب‌شده."
                >
                  <div className="grid grid-cols-[1fr_7rem] gap-2">
                    <input
                      className="input"
                      min={1}
                      onChange={(e) =>
                        setSegmentForm((x) => ({
                          ...x,
                          rules: {
                            ...x.rules,
                            minimumPurchaseAmount: e.target.value
                              ? Number(e.target.value)
                              : null,
                            purchaseCurrency: e.target.value
                              ? (x.rules.purchaseCurrency ?? "IRR")
                              : x.rules.purchaseCurrency,
                          },
                        }))
                      }
                      placeholder="مثلاً ۵۰۰۰۰۰۰"
                      type="number"
                      value={segmentForm.rules.minimumPurchaseAmount ?? ""}
                    />
                    <select
                      className="input bg-white"
                      onChange={(e) =>
                        setSegmentForm((x) => ({
                          ...x,
                          rules: {
                            ...x.rules,
                            purchaseCurrency: e.target.value || null,
                          },
                        }))
                      }
                      value={segmentForm.rules.purchaseCurrency ?? "IRR"}
                    >
                      <option value="IRR">ریال</option>
                      <option value="IRT">تومان</option>
                      <option value="USD">دلار</option>
                    </select>
                  </div>
                </Field>
                <Field
                  label="سطح باشگاه مشتریان"
                  hint="برای پیام یا پیشنهاد ویژه مشتریان وفادار."
                >
                  <select
                    className="input bg-white"
                    onChange={(e) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: {
                          ...x.rules,
                          vipLevel: (e.target.value ||
                            null) as CampaignSegmentRules["vipLevel"],
                          minimumVipLevel: null,
                        },
                      }))
                    }
                    value={segmentForm.rules.vipLevel ?? ""}
                  >
                    <option value="">بدون محدودیت</option>
                    <option value="Silver">نقره‌ای</option>
                    <option value="Gold">طلایی</option>
                    <option value="Platinum">پلاتینی</option>
                    <option value="Standard">عادی</option>
                  </select>
                </Field>
                <Field
                  label="حداقل سطح باشگاه مشتریان"
                  hint="این سطح و تمام سطح‌های بالاتر را انتخاب می‌کند؛ مناسب کمپین VIP."
                >
                  <select
                    className="input bg-white"
                    onChange={(e) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: {
                          ...x.rules,
                          minimumVipLevel: (e.target.value || null) as CampaignSegmentRules["minimumVipLevel"],
                          vipLevel: null,
                        },
                      }))
                    }
                    value={segmentForm.rules.minimumVipLevel ?? ""}
                  >
                    <option value="">بدون محدودیت</option>
                    <option value="Silver">از نقره‌ای به بالا</option>
                    <option value="Gold">از طلایی به بالا</option>
                    <option value="Platinum">فقط پلاتینی</option>
                  </select>
                </Field>
                <Field
                  label="حداقل مانده امتیاز"
                  hint="فقط مشتریانی که حداقل این میزان امتیاز قابل مصرف دارند."
                >
                  <input
                    className="input"
                    min={1}
                    onChange={(e) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: {
                          ...x.rules,
                          minimumLoyaltyPoints: e.target.value
                            ? Number(e.target.value)
                            : null,
                        },
                      }))
                    }
                    placeholder="مثلاً ۱۰۰"
                    type="number"
                    value={segmentForm.rules.minimumLoyaltyPoints ?? ""}
                  />
                </Field>
                <Field label="نوع علاقه‌مندی">
                  <select
                    className="input bg-white"
                    onChange={(e) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: {
                          ...x.rules,
                          interestType: e.target.value || null,
                        },
                      }))
                    }
                    value={segmentForm.rules.interestType ?? ""}
                  >
                    {interestOptions.map((x) => (
                      <option key={x.value} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="رفتار در صفحه اختصاصی">
                  <select
                    className="input bg-white"
                    onChange={(e) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: {
                          ...x.rules,
                          eventType: e.target.value || null,
                        },
                      }))
                    }
                    value={segmentForm.rules.eventType ?? ""}
                  >
                    {eventOptions.map((x) => (
                      <option key={x.value} value={x.value}>
                        {x.label}
                      </option>
                    ))}
                  </select>
                </Field>
                <div className="md:col-span-2 grid gap-3 sm:grid-cols-3">
                  <Toggle
                    checked={segmentForm.rules.hasMobile === true}
                    label="دارای شماره موبایل"
                    onChange={(value) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: { ...x.rules, hasMobile: value ? true : null },
                      }))
                    }
                  />
                  <Toggle
                    checked={segmentForm.rules.hasEmail === true}
                    label="دارای ایمیل"
                    onChange={(value) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: { ...x.rules, hasEmail: value ? true : null },
                      }))
                    }
                  />
                  <Toggle
                    checked={segmentForm.rules.excludePurchased === true}
                    label="خریداران حذف شوند"
                    hint="با فیلترهای خرید هم‌زمان استفاده نشود."
                    onChange={(value) =>
                      setSegmentForm((x) => ({
                        ...x,
                        rules: { ...x.rules, excludePurchased: value },
                      }))
                    }
                  />
                </div>
              </>
            ) : (
              <div className="md:col-span-2">
                <p className="mb-3 text-xs font-black text-slate-600">
                  اعضای ثابت
                </p>
                <div className="grid max-h-72 gap-2 overflow-y-auto rounded-2xl border border-slate-200 p-3 sm:grid-cols-2">
                  {contacts.data?.items.map((contact) => {
                    const selected = segmentForm.contactIds.includes(
                      contact.id,
                    );
                    const title =
                      [contact.firstName, contact.lastName]
                        .filter(Boolean)
                        .join(" ") ||
                      contact.contactPoints[0]?.value ||
                      "مشتری";
                    return (
                      <button
                        className={`flex items-center gap-3 rounded-xl border p-3 text-right text-xs font-bold ${selected ? "border-brand-300 bg-brand-50 text-brand-700" : "border-slate-100 bg-slate-50 text-slate-600"}`}
                        key={contact.id}
                        onClick={() =>
                          setSegmentForm((x) => ({
                            ...x,
                            contactIds: selected
                              ? x.contactIds.filter((id) => id !== contact.id)
                              : [...x.contactIds, contact.id],
                          }))
                        }
                        type="button"
                      >
                        <span
                          className={`grid size-5 place-items-center rounded ${selected ? "bg-brand-600 text-white" : "bg-white"}`}
                        >
                          {selected ? "✓" : ""}
                        </span>
                        {title}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className="mt-6 flex justify-end gap-2">
            <button
              className="rounded-xl bg-slate-100 px-5 py-3 text-sm font-black text-slate-600"
              onClick={() => setSegmentEditor(null)}
              type="button"
            >
              انصراف
            </button>
            <button
              className="rounded-xl bg-brand-600 px-6 py-3 text-sm font-black text-white disabled:opacity-50"
              disabled={saveSegment.isPending || !segmentForm.name.trim()}
              onClick={() => saveSegment.mutate()}
              type="button"
            >
              ذخیره گروه
            </button>
          </div>
        </Modal>
      ) : null}

      {templateEditor ? (
        <Modal
          onClose={() => setTemplateEditor(null)}
          title={templateEditor === "new" ? "ساخت قالب پیام" : "ویرایش قالب"}
        >
          <div className="space-y-4">
            <Field label="نام قالب">
              <input
                autoFocus
                className="input"
                onChange={(e) =>
                  setTemplateForm((x) => ({ ...x, name: e.target.value }))
                }
                value={templateForm.name}
              />
            </Field>
            <Field label="کانال">
              <select
                className="input bg-white"
                onChange={(e) =>
                  setTemplateForm((x) => ({
                    ...x,
                    channel: e.target.value as CampaignChannel,
                  }))
                }
                value={templateForm.channel}
              >
                <option value="sms">پیامک</option>
                <option value="email">ایمیل</option>
                <option value="social">شبکه اجتماعی</option>
              </select>
            </Field>
            <Field label="موضوع">
              <input
                className="input"
                onChange={(e) =>
                  setTemplateForm((x) => ({ ...x, subject: e.target.value }))
                }
                value={templateForm.subject}
              />
            </Field>
            <Field label="متن">
              <textarea
                className="input min-h-48 resize-y leading-8"
                onChange={(e) =>
                  setTemplateForm((x) => ({ ...x, body: e.target.value }))
                }
                value={templateForm.body}
              />
              <div className="mt-2 flex flex-wrap gap-2">
                {variables.map((token) => (
                  <button
                    className="rounded-lg bg-slate-100 px-2 py-1 font-sans text-[10px] font-bold"
                    key={token}
                    onClick={() =>
                      setTemplateForm((x) => ({
                        ...x,
                        body: `${x.body} ${token}`,
                      }))
                    }
                    type="button"
                  >
                    {token}
                  </button>
                ))}
              </div>
            </Field>
            <Toggle
              checked={templateForm.isCommercial}
              label="قالب پیام تجاری"
              onChange={(value) =>
                setTemplateForm((x) => ({ ...x, isCommercial: value }))
              }
            />
          </div>
          <button
            className="mt-6 w-full rounded-xl bg-brand-600 py-3 text-sm font-black text-white disabled:opacity-50"
            disabled={
              saveTemplate.isPending ||
              !templateForm.name.trim() ||
              !templateForm.body.trim()
            }
            onClick={() => saveTemplate.mutate()}
            type="button"
          >
            ذخیره قالب
          </button>
        </Modal>
      ) : null}

      {previewId ? (
        <Modal onClose={() => setPreviewId(null)} title="پیش‌نمایش مخاطبان">
          <div className="grid grid-cols-3 gap-3">
            {[
              ["کل مخاطبان", preview.data?.total ?? 0],
              ["مقصد معتبر", preview.data?.validDestinations ?? 0],
              ["اطلاعات ناقص", preview.data?.missingDestinations ?? 0],
            ].map(([label, value]) => (
              <div
                className="rounded-2xl bg-slate-50 p-3 text-center"
                key={String(label)}
              >
                <p className="text-[10px] font-bold text-slate-400">{label}</p>
                <p className="mt-1 text-xl font-black">{fa(Number(value))}</p>
              </div>
            ))}
          </div>
          <p
            className={`mt-4 rounded-xl p-3 text-xs font-bold ${preview.data?.channelReady ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}
          >
            {preview.data?.channelReady
              ? "کانال انتخاب‌شده آماده ارسال است."
              : "کانال آماده نیست؛ دسترسی لایسنس یا تنظیمات Provider را بررسی کنید."}
          </p>
          <div className="mt-4 space-y-2">
            {preview.data?.samples.map((x) => (
              <div
                className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 p-3 text-xs"
                key={x.contactId}
              >
                <b>{x.displayName}</b>
                <span className="font-sans text-slate-400" dir="ltr">
                  {x.destination || "اطلاعات تماس ناقص"}
                </span>
              </div>
            ))}
          </div>
        </Modal>
      ) : null}
      {recipientsId ? (
        <Modal
          onClose={() => setRecipientsId(null)}
          title="گزارش گیرندگان"
          wide
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[760px] text-right text-xs">
              <thead className="text-slate-400">
                <tr>
                  <th className="p-3">مخاطب</th>
                  <th className="p-3">مقصد</th>
                  <th className="p-3">وضعیت</th>
                  <th className="p-3">ارسال</th>
                  <th className="p-3">نتیجه</th>
                </tr>
              </thead>
              <tbody>
                {recipientRows.data?.map((x) => (
                  <tr className="border-t border-slate-100" key={x.id}>
                    <td className="p-3 font-black">{x.displayName}</td>
                    <td className="p-3 font-sans text-slate-500" dir="ltr">
                      {x.destination || "—"}
                    </td>
                    <td className="p-3">
                      <span className="rounded-full bg-slate-100 px-2 py-1 font-bold">
                        {recipientStatusLabels[x.status] ?? x.status}
                      </span>
                    </td>
                    <td className="p-3 text-slate-500">
                      {formatDateTime(x.sentAtUtc)}
                    </td>
                    <td className="max-w-72 p-3 text-slate-500">
                      {x.convertedAtUtc
                        ? "تبدیل ثبت شده"
                        : x.nextAttemptAtUtc
                          ? `تلاش بعدی: ${formatDateTime(x.nextAttemptAtUtc)}`
                          : x.error || "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {!recipientRows.isLoading && !recipientRows.data?.length ? (
            <Empty text="هنوز گیرنده‌ای برای این کمپین ثبت نشده است." />
          ) : null}
        </Modal>
      ) : null}
    </div>
  );
};

const SectionTitle = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}): React.ReactElement => (
  <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
    <div>
      <h2 className="text-lg font-black text-ink-950">{title}</h2>
      <p className="mt-1 text-xs leading-6 text-slate-400">{description}</p>
    </div>
    {action}
  </div>
);
const Skeleton = (): React.ReactElement => (
  <div className="grid gap-4 md:grid-cols-3">
    {[1, 2, 3].map((x) => (
      <div className="h-48 animate-pulse rounded-3xl bg-slate-200" key={x} />
    ))}
  </div>
);
const Empty = ({ text }: { text: string }): React.ReactElement => (
  <div className="mt-4 rounded-3xl border border-dashed border-slate-200 bg-white p-10 text-center text-sm font-bold text-slate-400">
    {text}
  </div>
);

const CampaignList = ({
  campaigns,
  isLoading,
  canManage,
  canSend,
  canApprove,
  onEdit,
  onPreview,
  onRecipients,
  onAction,
}: {
  campaigns: Campaign[];
  isLoading: boolean;
  canManage: boolean;
  canSend: boolean;
  canApprove: boolean;
  onEdit: (row: Campaign) => void;
  onPreview: (id: string) => void;
  onRecipients: (id: string) => void;
  onAction: (
    id: string,
    action: "publish" | "approve" | "pause" | "resume" | "cancel",
  ) => void;
}): React.ReactElement => (
  <section>
    <SectionTitle
      title="کمپین‌های سازمان"
      description="هر ارسال دارای مخاطب Snapshot، کنترل رضایت، محدودیت دفعات و گزارش مستقل است."
    />
    {isLoading ? (
      <Skeleton />
    ) : campaigns.length ? (
      <div className="grid gap-4 xl:grid-cols-2">
        {campaigns.map((row) => {
          const meta = statusMeta[row.status] ?? statusMeta.draft;
          const conversion = row.sent
            ? Math.round((row.converted / row.sent) * 100)
            : 0;
          return (
            <article
              className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
              key={row.id}
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-black text-ink-950">{row.name}</h3>
                    <span
                      className={`rounded-full px-2.5 py-1 text-[10px] font-black ${meta.tone}`}
                    >
                      {meta.label}
                    </span>
                  </div>
                  <p className="mt-2 text-xs text-slate-400">
                    {channelLabels[row.channel]} · {typeLabels[row.type]} ·{" "}
                    {formatDateTime(row.createdAtUtc)}
                  </p>
                </div>
                <span className="rounded-xl bg-slate-50 px-3 py-2 text-xs font-black text-slate-600">
                  {fa(conversion)}٪ تبدیل
                </span>
              </div>
              <div className="mt-5 grid grid-cols-4 gap-2">
                {[
                  ["مخاطب", row.totalRecipients],
                  ["ارسال", row.sent],
                  ["ناموفق", row.failed],
                  ["تبدیل", row.converted],
                ].map(([label, value]) => (
                  <div
                    className="rounded-xl bg-slate-50 p-2.5 text-center"
                    key={String(label)}
                  >
                    <p className="text-[10px] font-bold text-slate-400">
                      {label}
                    </p>
                    <p className="mt-1 font-black text-slate-800">
                      {fa(Number(value))}
                    </p>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap gap-2 border-t border-slate-100 pt-4">
                <button
                  className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-black text-slate-600"
                  onClick={() => onPreview(row.id)}
                  type="button"
                >
                  پیش‌نمایش
                </button>
                <button
                  className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-black text-slate-600"
                  onClick={() => onRecipients(row.id)}
                  type="button"
                >
                  گزارش گیرندگان
                </button>
                {canManage &&
                (row.status === "draft" || row.status === "paused") ? (
                  <button
                    className="rounded-xl bg-slate-100 px-3 py-2 text-xs font-black text-slate-600"
                    onClick={() => onEdit(row)}
                    type="button"
                  >
                    ویرایش
                  </button>
                ) : null}
                {canSend && row.status === "draft" ? (
                  <button
                    className="rounded-xl bg-brand-600 px-3 py-2 text-xs font-black text-white"
                    onClick={() => onAction(row.id, "publish")}
                    type="button"
                  >
                    انتشار
                  </button>
                ) : null}
                {canApprove && row.status === "pendingApproval" ? (
                  <button
                    className="rounded-xl bg-emerald-600 px-3 py-2 text-xs font-black text-white"
                    onClick={() => onAction(row.id, "approve")}
                    type="button"
                  >
                    تأیید اجرا
                  </button>
                ) : null}
                {canManage &&
                (row.status === "running" || row.status === "scheduled") ? (
                  <button
                    className="rounded-xl bg-amber-50 px-3 py-2 text-xs font-black text-amber-700"
                    onClick={() => onAction(row.id, "pause")}
                    type="button"
                  >
                    توقف
                  </button>
                ) : null}
                {canManage && row.status === "paused" ? (
                  <button
                    className="rounded-xl bg-emerald-50 px-3 py-2 text-xs font-black text-emerald-700"
                    onClick={() => onAction(row.id, "resume")}
                    type="button"
                  >
                    ادامه
                  </button>
                ) : null}
              </div>
            </article>
          );
        })}
      </div>
    ) : (
      <Empty text="هنوز کمپینی ساخته نشده است. از سناریوهای پیشنهادی یا دکمه ساخت کمپین شروع کنید." />
    )}
  </section>
);

const ConsentsPanel = ({
  canManage,
  onFeedback,
}: {
  canManage: boolean;
  onFeedback: (value: { tone: "ok" | "error"; text: string }) => void;
}): React.ReactElement => {
  const queryClient = useQueryClient();
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const rows = useQuery({
    queryKey: ["campaigns", "consents", search],
    queryFn: () => campaignsApi.consents(search),
  });
  const update = useMutation({
    mutationFn: ({
      row,
      channel,
      status,
    }: {
      row: CampaignContactConsent;
      channel: CampaignChannel;
      status: CampaignConsentStatus;
    }) =>
      campaignsApi.updateConsent({
        contactId: row.contactId,
        channel,
        purpose: "commercial",
        status,
        source: "ثبت توسط مدیر سازمان",
      }),
    onSuccess: async () => {
      onFeedback({ tone: "ok", text: "وضعیت رضایت مخاطب ثبت شد." });
      await queryClient.invalidateQueries({
        queryKey: ["campaigns", "consents"],
      });
    },
    onError: (error) =>
      onFeedback({ tone: "error", text: getErrorMessage(error) }),
  });
  return (
    <section>
      <SectionTitle
        description="برای پیام‌های بازاریابی، مجوز واقعی مشتری را به تفکیک کانال ثبت کنید. انتخاب «عدم ارسال» همیشه بر مجوز ارسال اولویت دارد."
        title="رضایت دریافت پیام"
      />
      <form
        className="mb-4 flex flex-col gap-2 rounded-2xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row"
        onSubmit={(event) => {
          event.preventDefault();
          setSearch(searchInput.trim());
        }}
      >
        <input
          className="input flex-1"
          onChange={(event) => setSearchInput(event.target.value)}
          placeholder="جست‌وجوی نام، موبایل یا ایمیل مشتری"
          value={searchInput}
        />
        <button
          className="rounded-xl bg-slate-950 px-5 py-3 text-xs font-black text-white"
          type="submit"
        >
          جست‌وجو
        </button>
      </form>
      {rows.isLoading ? <Skeleton /> : null}
      <div className="grid gap-4 xl:grid-cols-2">
        {rows.data?.map((row) => (
          <article
            className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5"
            key={row.contactId}
          >
            <div className="flex flex-col gap-1 border-b border-slate-100 pb-4 sm:flex-row sm:items-center sm:justify-between">
              <h3 className="font-black text-ink-950">{row.displayName}</h3>
              <span className="font-sans text-xs text-slate-400" dir="ltr">
                {row.mobile || row.email || "اطلاعات تماس ناقص"}
              </span>
            </div>
            <div className="mt-4 space-y-3">
              <ConsentControl
                canManage={canManage}
                channel="sms"
                disabled={!row.mobile || update.isPending}
                label="پیامک"
                onChange={(status) =>
                  update.mutate({ row, channel: "sms", status })
                }
                status={row.smsStatus}
              />
              <ConsentControl
                canManage={canManage}
                channel="email"
                disabled={!row.email || update.isPending}
                label="ایمیل"
                onChange={(status) =>
                  update.mutate({ row, channel: "email", status })
                }
                status={row.emailStatus}
              />
              <ConsentControl
                canManage={canManage}
                channel="social"
                disabled={!row.mobile || update.isPending}
                label="شبکه اجتماعی"
                onChange={(status) =>
                  update.mutate({ row, channel: "social", status })
                }
                status={row.socialStatus}
              />
            </div>
          </article>
        ))}
      </div>
      {!rows.isLoading && !rows.data?.length ? (
        <Empty text="مخاطبی با این مشخصات پیدا نشد." />
      ) : null}
    </section>
  );
};

const ConsentControl = ({
  label,
  status,
  disabled,
  canManage,
  onChange,
}: {
  label: string;
  channel: CampaignChannel;
  status: CampaignConsentStatus;
  disabled: boolean;
  canManage: boolean;
  onChange: (status: CampaignConsentStatus) => void;
}): React.ReactElement => (
  <div className="flex flex-col gap-2 rounded-2xl bg-slate-50 p-3 sm:flex-row sm:items-center sm:justify-between">
    <div>
      <b className="text-xs text-slate-700">{label}</b>
      <p className="mt-1 text-[10px] text-slate-400">
        {status === "optIn"
          ? "اجازه دریافت ثبت شده"
          : status === "optOut"
            ? "عدم ارسال ثبت شده"
            : "هنوز تعیین نشده"}
      </p>
    </div>
    {canManage ? (
      <div className="grid grid-cols-3 gap-1.5">
        <button
          className={`rounded-lg px-3 py-2 text-[10px] font-black ${status === "optIn" ? "bg-emerald-600 text-white" : "bg-white text-emerald-700"}`}
          disabled={disabled}
          onClick={() => onChange("optIn")}
          type="button"
        >
          مجاز است
        </button>
        <button
          className={`rounded-lg px-3 py-2 text-[10px] font-black ${status === "optOut" ? "bg-rose-600 text-white" : "bg-white text-rose-700"}`}
          disabled={disabled}
          onClick={() => onChange("optOut")}
          type="button"
        >
          عدم ارسال
        </button>
        <button
          className={`rounded-lg px-3 py-2 text-[10px] font-black ${status === "unknown" ? "bg-slate-600 text-white" : "bg-white text-slate-500"}`}
          disabled={disabled}
          onClick={() => onChange("unknown")}
          type="button"
        >
          نامشخص
        </button>
      </div>
    ) : null}
  </div>
);

const SettingsPanel = ({
  initial,
  suppressions,
  canManage,
  onSaved,
  onFeedback,
}: {
  initial: CampaignSettings;
  suppressions: Array<{
    id: string;
    channel: CampaignChannel;
    destination: string;
    reason: string;
  }>;
  canManage: boolean;
  onSaved: () => Promise<void>;
  onFeedback: (value: { tone: "ok" | "error"; text: string }) => void;
}): React.ReactElement => {
  const [form, setForm] = useState(initial);
  const [block, setBlock] = useState({
    channel: "sms" as CampaignChannel,
    destination: "",
    reason: "",
  });
  const save = useMutation({
    mutationFn: () => campaignsApi.updateSettings(form),
    onSuccess: async () => {
      onFeedback({ tone: "ok", text: "قواعد کنترل ارسال ذخیره شد." });
      await onSaved();
    },
    onError: (error) =>
      onFeedback({ tone: "error", text: getErrorMessage(error) }),
  });
  const add = useMutation({
    mutationFn: () => campaignsApi.addSuppression(block),
    onSuccess: async () => {
      setBlock({ channel: "sms", destination: "", reason: "" });
      onFeedback({ tone: "ok", text: "مقصد به فهرست عدم ارسال اضافه شد." });
      await onSaved();
    },
    onError: (error) =>
      onFeedback({ tone: "error", text: getErrorMessage(error) }),
  });
  const remove = useMutation({
    mutationFn: campaignsApi.deleteSuppression,
    onSuccess: onSaved,
  });
  const minuteToTime = (value: number): string =>
    `${String(Math.floor(value / 60)).padStart(2, "0")}:${String(value % 60).padStart(2, "0")}`;
  const timeToMinute = (value: string): number => {
    const [h, m] = value.split(":").map(Number);
    return h * 60 + m;
  };
  return (
    <section className="grid gap-5 xl:grid-cols-[1.2fr_.8fr]">
      <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionTitle
          title="قواعد ایمنی و دفعات ارسال"
          description="این قواعد بین همه کمپین‌های شرکت مشترک‌اند و از ارسال مزاحم یا خارج از ساعت جلوگیری می‌کنند."
        />
        <div className="grid gap-3 sm:grid-cols-2">
          <Toggle
            checked={form.requireApproval}
            label="تأیید قبل از اجرا"
            hint="کمپین پس از انتشار باید توسط دارنده دسترسی تأیید شود."
            onChange={(value) =>
              setForm((x) => ({ ...x, requireApproval: value }))
            }
          />
          <Toggle
            checked={form.requireCommercialConsent}
            label="رضایت صریح تجاری"
            hint="بدون Opt-in، پیام تجاری ارسال نمی‌شود."
            onChange={(value) =>
              setForm((x) => ({ ...x, requireCommercialConsent: value }))
            }
          />
          <Toggle
            checked={form.quietHoursEnabled}
            label="ساعات سکوت"
            hint="ارسال‌ها تا پایان بازه سکوت نگه داشته می‌شوند."
            onChange={(value) =>
              setForm((x) => ({ ...x, quietHoursEnabled: value }))
            }
          />
          <div className="grid grid-cols-2 gap-2 rounded-2xl border border-slate-200 p-3">
            <Field label="شروع سکوت">
              <input
                className="input"
                onChange={(e) =>
                  setForm((x) => ({
                    ...x,
                    quietStartMinute: timeToMinute(e.target.value),
                  }))
                }
                type="time"
                value={minuteToTime(form.quietStartMinute)}
              />
            </Field>
            <Field label="پایان سکوت">
              <input
                className="input"
                onChange={(e) =>
                  setForm((x) => ({
                    ...x,
                    quietEndMinute: timeToMinute(e.target.value),
                  }))
                }
                type="time"
                value={minuteToTime(form.quietEndMinute)}
              />
            </Field>
          </div>
        </div>
        <div className="mt-4 grid gap-3 sm:grid-cols-4">
          <Field label="پیامک">
            <input
              className="input"
              min={0}
              onChange={(e) =>
                setForm((x) => ({ ...x, smsLimit: Number(e.target.value) }))
              }
              type="number"
              value={form.smsLimit}
            />
          </Field>
          <Field label="ایمیل">
            <input
              className="input"
              min={0}
              onChange={(e) =>
                setForm((x) => ({ ...x, emailLimit: Number(e.target.value) }))
              }
              type="number"
              value={form.emailLimit}
            />
          </Field>
          <Field label="شبکه اجتماعی">
            <input
              className="input"
              min={0}
              onChange={(e) =>
                setForm((x) => ({ ...x, socialLimit: Number(e.target.value) }))
              }
              type="number"
              value={form.socialLimit}
            />
          </Field>
          <Field label="در بازه چند روز">
            <input
              className="input"
              min={1}
              onChange={(e) =>
                setForm((x) => ({ ...x, rollingDays: Number(e.target.value) }))
              }
              type="number"
              value={form.rollingDays}
            />
          </Field>
        </div>
        <p className="mt-2 text-[11px] text-slate-400">
          عدد صفر برای هر کانال یعنی بدون سقف تعداد؛ ساعات بر اساس منطقه زمانی
          ایران محاسبه می‌شوند.
        </p>
        {canManage ? (
          <button
            className="mt-5 rounded-xl bg-brand-600 px-6 py-3 text-sm font-black text-white disabled:opacity-50"
            disabled={save.isPending}
            onClick={() => save.mutate()}
            type="button"
          >
            ذخیره قواعد
          </button>
        ) : null}
      </article>
      <article className="rounded-3xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <SectionTitle
          title="فهرست عدم ارسال"
          description="شماره، ایمیل یا شناسه‌ای که نباید هیچ کمپینی در کانال انتخابی دریافت کند."
        />
        {canManage ? (
          <div className="space-y-3 rounded-2xl bg-slate-50 p-3">
            <select
              className="input bg-white"
              onChange={(e) =>
                setBlock((x) => ({
                  ...x,
                  channel: e.target.value as CampaignChannel,
                }))
              }
              value={block.channel}
            >
              <option value="sms">پیامک</option>
              <option value="email">ایمیل</option>
              <option value="social">شبکه اجتماعی</option>
            </select>
            <input
              className="input bg-white font-sans"
              dir="ltr"
              onChange={(e) =>
                setBlock((x) => ({ ...x, destination: e.target.value }))
              }
              placeholder="شماره یا ایمیل"
              value={block.destination}
            />
            <input
              className="input bg-white"
              onChange={(e) =>
                setBlock((x) => ({ ...x, reason: e.target.value }))
              }
              placeholder="دلیل عدم ارسال"
              value={block.reason}
            />
            <button
              className="w-full rounded-xl bg-slate-950 py-2.5 text-xs font-black text-white disabled:opacity-50"
              disabled={
                !block.destination.trim() ||
                !block.reason.trim() ||
                add.isPending
              }
              onClick={() => add.mutate()}
              type="button"
            >
              افزودن
            </button>
          </div>
        ) : null}
        <div className="mt-4 max-h-80 space-y-2 overflow-y-auto">
          {suppressions.map((row) => (
            <div
              className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 p-3"
              key={row.id}
            >
              <div className="min-w-0">
                <p
                  className="truncate font-sans text-xs font-bold text-slate-700"
                  dir="ltr"
                >
                  {row.destination}
                </p>
                <p className="mt-1 truncate text-[11px] text-slate-400">
                  {channelLabels[row.channel]} · {row.reason}
                </p>
              </div>
              {canManage ? (
                <button
                  className="text-lg text-rose-500"
                  onClick={() => remove.mutate(row.id)}
                  type="button"
                >
                  ×
                </button>
              ) : null}
            </div>
          ))}
          {!suppressions.length ? (
            <p className="py-8 text-center text-xs text-slate-400">
              فهرست خالی است.
            </p>
          ) : null}
        </div>
      </article>
    </section>
  );
};
